import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Layout } from '~/components/UI';
import { LoadingTopBar } from '~/components/UI/LoadingTopBar';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { DocumentsDeadlines } from '~/_domains/reports/deadlines';
import { loadDocumentsDeadlines } from '~/_domains/reports/deadlines/actions';
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  DEADLINES_FILTERS_KEY,
  getDefaultDeadlinesFilters,
  getDeadlinesRequest,
  isDeadlinesMonthDefault,
} from '~/_domains/reports/deadlines/filters';

export const ScreensDocumentsDeadlines = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const isFirstLoad = useRef(true);

  // Filters kept across refreshes for a few minutes (per tenant/user/page).
  const persisted = usePersistedFilters(DEADLINES_FILTERS_KEY, getDefaultDeadlinesFilters);
  const applied = persisted.filters;

  // Loads the restored (or current) month on mount, and again when it changes.
  useEffect(() => {
    loadData();
  }, [applied.month]);

  async function loadData() {
    const firstLoad = isFirstLoad.current;
    isFirstLoad.current = false;
    // The IndexedDB cache is keyed by route only, so it only ever holds the
    // current month; using it for another month would flash wrong data.
    const usesCache = isDeadlinesMonthDefault(applied, persisted.defaults);

    try {
      if (firstLoad && usesCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.documentsDeadlines,
          dispatch,
          pathname,
        });
      }
      const request = getDeadlinesRequest(applied);
      // First load keeps the top bar only (as before); later changes use the default loader.
      if (firstLoad) request.showLoading = false;
      const { payload } = await dispatch(loadDocumentsDeadlines(request));
      if (usesCache) {
        await LocalCacheHandler.save({
          pathname,
          data: payload.data.tasks,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <LoadingTopBar isActive={isLoading} />
      <Layout>
        <DocumentsDeadlines persisted={persisted} />
      </Layout>
    </div>
  );
};
